import { Box, Button, Form, TextField } from '@qbe/ads-core';
import React from 'react';
import { Controller } from 'react-hook-form';

import FormSpacer from '~/components-ds/FormSpacer';
import { useBreakpoint } from '~/hooks-ds/useBreakpoint';

import { stringOnlyWithSpaces, useAppContent, useAppDispatch, useAppSelector } from '../../hooks';
import { useFormWithAnalytics } from '../../hooks/useFormWithAnalytics';
import { updateIsPaymentInProgress } from '../../slices/payments';
import {
  updateBankAccountBsb,
  updateBankAccountName,
  updateBankAccountNumber,
} from '../../slices/payments';

const bsbPattern = /^\d{6}$/;
const accountNumberPattern = /^(\d{8}|\d{9})$/;

interface BankAccountFormProps {
  onSubmitPayment: () => Promise<unknown>;
}

type BankAccountFormFields = {
  name: string;
  bsb: string;
  accountNumber: string;
};

const BankAccountForm = ({ onSubmitPayment }: BankAccountFormProps) => {
  const { getTextContent } = useAppContent({ basePath: 'dsme.payments' });
  const { isMobile } = useBreakpoint();
  const { control, handleSubmit, formState } = useFormWithAnalytics<BankAccountFormFields>({
    mode: 'onChange',
    values: {
      name: '',
      bsb: '',
      accountNumber: '',
    },
  });
  const dispatch = useAppDispatch();
  const isPaymentInProgress = useAppSelector(
    (state) => state.payments?.isPaymentInProgress ?? false
  );

  const onSubmit = handleSubmit(async (data: BankAccountFormFields | undefined) => {
    if (!data) return;

    // Guard: prevent double-click while processing
    if (isPaymentInProgress) return;

    try {
      // Set Redux state FIRST for immediate visual feedback
      dispatch(updateIsPaymentInProgress(true));

      dispatch(updateBankAccountName(data.name));
      dispatch(updateBankAccountBsb(data.bsb));
      dispatch(updateBankAccountNumber(data.accountNumber));
      await onSubmitPayment();
    } finally {
      // Reset flag after payment attempt completes (success or error)
      dispatch(updateIsPaymentInProgress(false));
    }
  });

  return (
    <Form onSubmit={onSubmit}>
      <Box textAlign="center">
        <Controller
          control={control}
          name="name"
          render={({ field, fieldState }) => (
            <TextField
              {...field}
              id="name"
              name="name"
              label="Account holder name"
              type="text"
              error={fieldState.invalid}
              displayIcon
              helperText={fieldState.invalid ? 'Invalid name' : 'eg: John Smith'}
              required
            />
          )}
          rules={{
            required: true,
            pattern: stringOnlyWithSpaces,
          }}
        />
        <FormSpacer variant="input" />
        <Box
          display="flex"
          flexDirection={isMobile ? 'column' : 'row'}
          maxWidth="400px"
          justifyContent="space-between"
          gap={isMobile ? '' : 4}
        >
          <Controller
            control={control}
            name="bsb"
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                id="bsb"
                name="bsb"
                label="BSB"
                type="tel"
                error={fieldState.invalid}
                displayIcon
                helperText={fieldState.invalid ? 'Invalid bsb' : 'eg: 123456'}
                required
              />
            )}
            rules={{
              required: true,
              pattern: bsbPattern,
            }}
          />
          {isMobile && <FormSpacer variant="input" />}
          <Controller
            control={control}
            name="accountNumber"
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                id="accountNumber"
                label="Account number"
                type="tel"
                error={fieldState.invalid}
                displayIcon
                helperText={fieldState.invalid ? 'Invalid account number' : 'eg: 123456789'}
                required
              />
            )}
            rules={{
              required: true,
              pattern: accountNumberPattern,
            }}
          />
        </Box>
      </Box>

      <Box textAlign="center">
        <Button
          disabled={!formState.isValid || formState.isLoading || isPaymentInProgress}
          type="submit"
          label={getTextContent('buttons.buyNow')}
        />
      </Box>
    </Form>
  );
};

export default BankAccountForm;
